import { useEffect, useRef } from "react";
import { useFormikContext } from "formik";
import $ from "jquery";
import "select2/dist/css/select2.min.css";
import "select2/dist/js/select2.min.js";
import PropTypes from "prop-types";
import {
  buildTeacherAvatarHtml,
  escapeHtml,
  resolveProfilePic,
} from "utils/teacherAvatar";

const toStr = (v) => (v === null || v === undefined ? "" : String(v));

function formatOptionWithAvatar(state) {
  if (!state.id) return state.text;
  const el = state.element;
  const pic = el ? el.getAttribute("data-profile-pic") || "" : "";
  const text = escapeHtml(state.text ?? "");
  const avatar = buildTeacherAvatarHtml({ profilePic: pic, fullName: state.text }, 22);
  return $(
    `<span class="select2-option-with-avatar">${avatar}<span class="select2-option-label">${text}</span></span>`
  );
}

export default function Select2Field({
  name,
  options = [],
  placeholder = "",
  disabled = false,
  value,       // optional external controlled value
  onChange,
  allowClear = true,
  multiple = false,
  dropdownParent, // optional DOM element (for modals)
  showAvatars = false,
}) {
  let formik = null;
  try {
    formik = useFormikContext();
  } catch {
    formik = null;
  }

  const selectRef = useRef(null);

  const getSelected = () => {
    const v = value !== undefined ? value : formik?.values?.[name];
    if (multiple) {
      if (Array.isArray(v)) return v.map(toStr);
      if (typeof v === "string" && v.length) return v.split(","); // fallback if stringified
      return [];
    }
    return toStr(v ?? "");
  };

  const buildDomOptions = ($select) => {
    $select.empty();
    options.forEach((o) => {
      const id = toStr(o.id);
      const text = o.text ?? id;
      const option = new Option(text, id, false, false);
      const pic = resolveProfilePic(o);
      if (pic) {
        option.setAttribute("data-profile-pic", pic);
      }
      $select.append(option);
    });
  };

  useEffect(() => {
    const $select = $(selectRef.current);

    if ($select.hasClass("select2-hidden-accessible")) {
      $select.off(".select2Field");
      $select.select2("destroy");
    }

    buildDomOptions($select);

    const select2Opts = {
      placeholder,
      width: "100%",
      allowClear,
      multiple,
      dropdownParent: dropdownParent ? $(dropdownParent) : undefined,
    };

    if (showAvatars) {
      select2Opts.templateResult = formatOptionWithAvatar;
      select2Opts.templateSelection = formatOptionWithAvatar;
      select2Opts.escapeMarkup = (markup) => markup;
    }

    $select.select2(select2Opts);

    $select.on("change.select2Field", (e) => {
      const val = multiple ? $select.val() || [] : toStr(e.target.value);
      if (formik) {
        formik.setFieldValue(name, val);
      }
      if (onChange) onChange(val);
    });

    const selected = getSelected();
    $select.val(selected).trigger("change.select2");

    return () => {
      if ($select.hasClass("select2-hidden-accessible")) {
        $select.off(".select2Field");
        $select.select2("destroy");
      }
    };
  }, [JSON.stringify(options), placeholder, dropdownParent, multiple, showAvatars]);

  useEffect(() => {
    const $select = $(selectRef.current);
    if (!$select.length) return;

    const selected = getSelected();
    $select.val(selected).trigger("change.select2");
  }, [JSON.stringify(value ?? formik?.values?.[name] ?? []), JSON.stringify(options)]);

  useEffect(() => {
    $(selectRef.current).prop("disabled", !!disabled);
  }, [disabled]);

  return (
    <select
      ref={selectRef}
      name={name}
      multiple={multiple}
      style={{ width: "100%" }}
    />
  );
}

Select2Field.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.any,
      text: PropTypes.string.isRequired,
      profilePic: PropTypes.string,
      ProfilePic: PropTypes.string,
    })
  ).isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  value: PropTypes.any,
  onChange: PropTypes.func,
  allowClear: PropTypes.bool,
  multiple: PropTypes.bool,
  dropdownParent: PropTypes.any,
  showAvatars: PropTypes.bool,
};
